import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Typography,
  Button,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import ImageIcon from '@material-ui/icons/Image';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';
import {
  COMPANY_SIGNED_IN_BACKGROUND_COLOR,
  COMPANY_STATION_ID,
  GENERAL_TEXT_COLOR,
  BASE_S3,
  BASE_API,
  USER_TOKEN,
  BUTTON_BACKGROUND_COLOR,
  BUTTON_TEXT_COLOR,
  HEADER_TEXT_COLOR,
} from '../../../../../config/consts';
import { getClassifiedAds, getCategories } from './classifiedAdsActions';
import LoadingProgress from '../../../../../common/components/progress/loading';
import {
  Container,
  AdsGrid,
  AdCard,
  AdCardImage,
  AdCardContent,
  CategoryChip,
  FilterBar,
  FilterChip,
  NoImagePlaceholder,
} from './styles';

const btnBg = BUTTON_BACKGROUND_COLOR || GENERAL_TEXT_COLOR;
const btnText = BUTTON_TEXT_COLOR || '#fff';

function ClassifiedAds(props) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortRecent, setSortRecent] = useState(false);
  const [adsList, setAdsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    cel: '',
    email: '',
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const fileInputRef = useRef(null);

  const getHeaders = () => ({
    Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
  });

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${BASE_API}/station-classified-ads/${COMPANY_STATION_ID}`, { headers: getHeaders() }),
      axios.get(`${BASE_API}/classified-ads-all-categories`, { headers: getHeaders() }),
    ])
      .then(([adsRes, catsRes]) => {
        setAdsList(adsRes.data || []);
        setCategoriesList(catsRes.data || []);
        setLoading(false);
      })
      .catch((e) => {
        console.log('[ClassifiedAds] Error:', e.response?.status, e.response?.data || e.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let filteredAds = selectedCategory
    ? adsList.filter((ad) => ad.category_id === selectedCategory)
    : adsList;

  if (sortRecent) {
    filteredAds = [...filteredAds].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }

  const getAdImage = (ad) => {
    if (ad.images && ad.images.length > 0) {
      const validImage = ad.images.find(
        (i) => i.image && !i.image.startsWith('file:///')
      );
      if (validImage) {
        const img = validImage.image;
        if (img.startsWith('http')) return img;
        return `${BASE_S3}/${img}`;
      }
    }
    return null;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleFormChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (selectedImages.length + files.length > 5) {
      toastr.warning('Atenção', 'Máximo de 5 imagens por anúncio');
      return;
    }
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSelectedImages((prev) => [
          ...prev,
          { file, preview: ev.target.result, base64: ev.target.result },
        ]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadImageToS3 = async (base64Data) => {
    const response = await axios.post(
      `${BASE_API}/upload-image-to-s3`,
      { base64: base64Data },
      { headers: getHeaders() }
    );
    return response.data.url;
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toastr.warning('Atenção', 'Informe o título do anúncio');
      return;
    }
    if (!formData.description.trim()) {
      toastr.warning('Atenção', 'Informe a descrição do anúncio');
      return;
    }
    if (!formData.category_id) {
      toastr.warning('Atenção', 'Selecione uma categoria');
      return;
    }

    setSubmitting(true);
    try {
      const images = [];
      for (const img of selectedImages) {
        const s3Path = await uploadImageToS3(img.base64);
        images.push({ image: s3Path });
      }

      await axios.post(
        `${BASE_API}/create-ads`,
        {
          station_id: COMPANY_STATION_ID,
          title: formData.title,
          description: formData.description,
          category_id: formData.category_id,
          cel: formData.cel,
          email: formData.email,
          images,
        },
        { headers: getHeaders() }
      );

      toastr.success('Sucesso', 'Anúncio criado com sucesso!');
      setModalOpen(false);
      setFormData({ title: '', description: '', category_id: '', cel: '', email: '' });
      setSelectedImages([]);
      fetchData();
    } catch (e) {
      console.log('[ClassifiedAds] Create error:', e.response?.data || e.message);
      toastr.error('Erro', 'Erro ao criar anúncio');
    }
    setSubmitting(false);
  };

  return (
    <section
      style={{
        backgroundColor: COMPANY_SIGNED_IN_BACKGROUND_COLOR,
        minHeight: '100vh',
      }}
    >
      <Container>
        <Typography variant="h4" style={{ color: HEADER_TEXT_COLOR || GENERAL_TEXT_COLOR, marginBottom: 8 }}>
          Classificados
        </Typography>

        <FilterBar>
          <FilterChip
            active={!selectedCategory && !sortRecent}
            activeColor={btnBg}
            onClick={() => {
              setSelectedCategory(null);
              setSortRecent(false);
            }}
          >
            Todos
          </FilterChip>
          <FilterChip
            active={sortRecent}
            activeColor={btnBg}
            onClick={() => setSortRecent(!sortRecent)}
          >
            Recentes
          </FilterChip>
          {categoriesList.map((cat) => (
            <FilterChip
              key={cat.id}
              active={selectedCategory === cat.id}
              activeColor={btnBg}
              onClick={() => {
                setSelectedCategory(
                  selectedCategory === cat.id ? null : cat.id
                );
                setSortRecent(false);
              }}
            >
              {cat.category}
            </FilterChip>
          ))}
        </FilterBar>

        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '40px 0',
            }}
          >
            <LoadingProgress>Carregando classificados...</LoadingProgress>
          </div>
        ) : filteredAds.length > 0 ? (
          <AdsGrid>
            {filteredAds.map((ad) => {
              const imageUrl = getAdImage(ad);
              return (
                <AdCard
                  key={ad.id}
                  onClick={() =>
                    props.history.push(`/homepage/classificados/${ad.id}`)
                  }
                >
                  <AdCardImage src={imageUrl}>
                    {!imageUrl && (
                      <NoImagePlaceholder>
                        <ImageIcon style={{ fontSize: 64, color: '#ccc' }} />
                      </NoImagePlaceholder>
                    )}
                  </AdCardImage>
                  <AdCardContent>
                    <Typography
                      variant="h6"
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: GENERAL_TEXT_COLOR,
                        marginBottom: 4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ad.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        color: '#888',
                        marginBottom: 8,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ad.description}
                    </Typography>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      {ad.user && (
                        <Typography
                          variant="caption"
                          style={{ color: '#aaa' }}
                        >
                          {ad.user.name}
                        </Typography>
                      )}
                      <Typography variant="caption" style={{ color: '#aaa' }}>
                        {formatDate(ad.createdAt)}
                      </Typography>
                    </div>
                    {ad.comments && ad.comments.length > 0 && (
                      <Typography
                        variant="caption"
                        style={{ color: '#999', marginTop: 4, display: 'block' }}
                      >
                        {ad.comments.length}{' '}
                        {ad.comments.length === 1
                          ? 'comentário'
                          : 'comentários'}
                      </Typography>
                    )}
                  </AdCardContent>
                </AdCard>
              );
            })}
          </AdsGrid>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <ImageIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
            <Typography
              variant="h6"
              style={{ color: '#999' }}
            >
              Nenhum anúncio encontrado
            </Typography>
            <Typography
              variant="body2"
              style={{ color: '#bbb', marginTop: 8 }}
            >
              {selectedCategory
                ? 'Não há anúncios nesta categoria.'
                : 'Ainda não há anúncios publicados.'}
            </Typography>
          </div>
        )}
      </Container>

      <Fab
        aria-label="add"
        onClick={() => setModalOpen(true)}
        style={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          backgroundColor: btnBg,
          color: btnText,
          zIndex: 1000,
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
        }}
      >
        <AddIcon />
      </Fab>

      {modalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
            }}
            onClick={() => !submitting && setModalOpen(false)}
          />
          <div
            style={{
              position: 'relative',
              backgroundColor: '#fff',
              borderRadius: 12,
              width: '100%',
              maxWidth: 540,
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              margin: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '24px 28px 0',
              }}
            >
              <Typography variant="h6" style={{ fontWeight: 600, color: GENERAL_TEXT_COLOR }}>
                Criar Anúncio
              </Typography>
              <IconButton onClick={() => !submitting && setModalOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </div>

            <div style={{ padding: '24px 28px' }}>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#555' }}>
                  Título *
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ex: iPhone 15 Pro Max 256GB"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  disabled={submitting}
                  maxLength={100}
                  style={{ borderRadius: 8 }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#555' }}>
                  Descrição *
                </label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Descreva seu anúncio com detalhes..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={submitting}
                  style={{ borderRadius: 8, resize: 'vertical' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#555' }}>
                  Categoria *
                </label>
                <select
                  className="form-control"
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  disabled={submitting}
                  style={{ borderRadius: 8 }}
                >
                  <option value="">Selecione uma categoria</option>
                  {categoriesList.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#555' }}>
                    Telefone / WhatsApp
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="(00) 00000-0000"
                    value={formData.cel}
                    onChange={(e) => setFormData({ ...formData, cel: e.target.value })}
                    disabled={submitting}
                    style={{ borderRadius: 8 }}
                  />
                </div>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#555' }}>
                    E-mail (opcional)
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="email@exemplo.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={submitting}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'block', marginBottom: 10, fontSize: 14, fontWeight: 500, color: '#555' }}>
                  Imagens (máx. 5)
                </label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {selectedImages.map((img, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: 'relative',
                        width: 88,
                        height: 88,
                        borderRadius: 10,
                        overflow: 'hidden',
                        border: '2px solid #e0e0e0',
                      }}
                    >
                      <img
                        src={img.preview}
                        alt={`img-${idx}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeImage(idx)}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          padding: 2,
                        }}
                      >
                        <CloseIcon style={{ fontSize: 14 }} />
                      </IconButton>
                    </div>
                  ))}
                  {selectedImages.length < 5 && (
                    <div
                      onClick={() => !submitting && fileInputRef.current?.click()}
                      style={{
                        width: 88,
                        height: 88,
                        borderRadius: 10,
                        border: '2px dashed #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        transition: 'border-color 0.2s',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <AddPhotoAlternateIcon style={{ color: '#bbb', fontSize: 28 }} />
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleImageSelect}
                />
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
                padding: '16px 28px 24px',
                borderTop: '1px solid #eee',
              }}
            >
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setModalOpen(false)}
                disabled={submitting}
                style={{ borderRadius: 8, padding: '8px 24px', fontSize: 14 }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  backgroundColor: btnBg,
                  color: btnText,
                  borderRadius: 8,
                  padding: '8px 32px',
                  fontSize: 14,
                  fontWeight: 500,
                  border: 'none',
                  minWidth: 130,
                }}
              >
                {submitting ? (
                  <CircularProgress size={18} style={{ color: btnText }} />
                ) : (
                  'Publicar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    classifiedAds: state.classifiedAds,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({ getClassifiedAds, getCategories }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(ClassifiedAds);
